import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Chip, 
  Card,
  CardContent,
  CardMedia,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { 
  CalendarToday, 
  LocationOn, 
  Business, 
  AttachMoney,
  ArrowBack,
  Edit,
  Save,
  Cancel,
  Delete,
  People,
  TrendingUp,
  Star,
  CheckCircle,
  Warning,
  Error,
  Add,
  Language,
  Image,
  OpenInNew
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  getEventDetails, 
  updateEvent, 
  deleteEvent, 
  getEventAnalytics,
  addEventAdminSponsor,
  getEventAdminSponsors,
  updateEventAdminSponsor,
  deleteEventAdminSponsor
} from '../services/api';

const statusOptions = [
  { value: 'pending', label: 'Pending', color: 'warning' },
  { value: 'accepted', label: 'Accepted', color: 'success' },
  { value: 'rejected', label: 'Rejected', color: 'error' },
];

interface AdminSponsor {
  _id: string;
  name: string;
  website?: string;
  imageUrl?: string;
}

const EventDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<any>(null);
  const [analytics, setAnalytics] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Editable state for event details
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    status: '',
    featured: false,
    takeRegistrations: false
  });

  // Admin Sponsors state
  const [sponsors, setSponsors] = useState<AdminSponsor[]>([]);
  const [sponsorsLoading, setSponsorsLoading] = useState(false);
  const [sponsorDialogOpen, setSponsorDialogOpen] = useState(false);
  const [editingSponsorId, setEditingSponsorId] = useState<string | null>(null);
  const [sponsorFormData, setSponsorFormData] = useState({
    name: '',
    website: '',
    imageUrl: ''
  });
  const [sponsorErrors, setSponsorErrors] = useState<any>({});
  const [sponsorSubmitting, setSponsorSubmitting] = useState(false);

  const loadEventDetails = async () => {
    try {
      setLoading(true);
      const response = await getEventDetails(id!);
      if (response.success) {
        setEvent(response.data);
        setEditData({
          status: response.data.status || 'pending',
          featured: response.data.featured || false,
          takeRegistrations: response.data.takeRegistrations !== false
        });
      } else {
        setError('Failed to load event details');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error loading event details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const response = await getEventAnalytics(id!);
      if (response.success) {
        setAnalytics(response.data);
      }
    } catch (err: any) {
      console.error('Error loading analytics:', err);
    }
  };

  const loadSponsors = async () => {
    try {
      setSponsorsLoading(true);
      const response = await getEventAdminSponsors(id!);
      if (response.success) {
        setSponsors(response.data);
      }
    } catch (err: any) {
      console.error('Error loading sponsors:', err);
      showAlert('error', 'Failed to load sponsors');
    } finally {
      setSponsorsLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadEventDetails();
      loadAnalytics();
      loadSponsors();
    }
  }, [id]);

  const showAlert = (type: 'success' | 'error', message: string) => {
    if (type === 'success') {
      setSuccess(message);
      setTimeout(() => setSuccess(null), 3000);
    } else {
      setError(message);
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
    if (isEditing) {
      setEditData({
        status: event.status || 'pending',
        featured: event.featured || false,
        takeRegistrations: event.takeRegistrations !== false
      });
    }
  };

  const handleSave = async () => {
    try {
      setUpdating(true);
      const response = await updateEvent(id!, editData);
      if (response.success) {
        setEvent(response.data);
        setIsEditing(false);
        showAlert('success', 'Event updated successfully');
      } else {
        showAlert('error', response.message || 'Failed to update event');
      }
    } catch (err: any) {
      showAlert('error', err.response?.data?.message || 'Error updating event');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this event? This action cannot be undone.')) {
      return;
    }

    try {
      setUpdating(true);
      const response = await deleteEvent(id!);
      if (response.success) {
        showAlert('success', 'Event deleted successfully');
        setTimeout(() => {
          navigate('/event-analytics');
        }, 1000);
      } else {
        showAlert('error', response.message || 'Failed to delete event');
      }
    } catch (err: any) {
      showAlert('error', err.response?.data?.message || 'Error deleting event');
    } finally {
      setUpdating(false);
    }
  };

  // Sponsor Management Functions
  const validateSponsorForm = () => {
    const newErrors: any = {};
    if (!sponsorFormData.name.trim()) {
      newErrors.name = 'Sponsor name is required';
    }
    if (sponsorFormData.website && !isValidUrl(sponsorFormData.website)) {
      newErrors.website = 'Please enter a valid URL';
    }
    if (sponsorFormData.imageUrl && !isValidUrl(sponsorFormData.imageUrl)) {
      newErrors.imageUrl = 'Please enter a valid image URL';
    }
    setSponsorErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (string: string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleSponsorSubmit = async () => {
    if (!validateSponsorForm()) return;

    try {
      setSponsorSubmitting(true);

      if (editingSponsorId) {
        const response = await updateEventAdminSponsor(id!, editingSponsorId, sponsorFormData);
        if (response.success) {
          setSponsors(prev => prev.map(s => s._id === editingSponsorId ? response.sponsor : s));
          showAlert('success', 'Sponsor updated successfully');
        } else {
          showAlert('error', response.message || 'Failed to update sponsor');
        }
      } else {
        const response = await addEventAdminSponsor(id!, sponsorFormData);
        if (response.success) {
          setSponsors(prev => [...prev, response.sponsor]);
          showAlert('success', 'Sponsor added successfully');
        } else {
          showAlert('error', response.message || 'Failed to add sponsor');
        }
      }

      handleCloseSponsorDialog();
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || (editingSponsorId ? 'Failed to update sponsor' : 'Failed to add sponsor'));
    } finally {
      setSponsorSubmitting(false);
    }
  };

  const handleDeleteSponsor = async (sponsorId: string) => {
    if (!window.confirm('Are you sure you want to delete this sponsor?')) return;

    try {
      setSponsorsLoading(true);
      const response = await deleteEventAdminSponsor(id!, sponsorId);
      if (response.success) {
        setSponsors(prev => prev.filter(s => s._id !== sponsorId));
        showAlert('success', 'Sponsor deleted successfully');
      } else {
        showAlert('error', response.message || 'Failed to delete sponsor');
      }
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to delete sponsor');
    } finally {
      setSponsorsLoading(false);
    }
  };

  const handleOpenSponsorDialog = (sponsor: AdminSponsor | null = null) => {
    if (sponsor) {
      setEditingSponsorId(sponsor._id);
      setSponsorFormData({
        name: sponsor.name,
        website: sponsor.website || '',
        imageUrl: sponsor.imageUrl || ''
      });
    } else {
      setEditingSponsorId(null);
      setSponsorFormData({ name: '', website: '', imageUrl: '' });
    }
    setSponsorErrors({});
    setSponsorDialogOpen(true);
  };

  const handleCloseSponsorDialog = () => {
    setSponsorDialogOpen(false);
    setEditingSponsorId(null);
    setSponsorFormData({ name: '', website: '', imageUrl: '' });
    setSponsorErrors({});
  };

  const handleSponsorInputChange = (field: string, value: string) => {
    setSponsorFormData(prev => ({ ...prev, [field]: value }));
    if (sponsorErrors[field]) {
      setSponsorErrors((prev: any) => ({ ...prev, [field]: null }));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'accepted': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <Warning />;
      case 'accepted': return <CheckCircle />;
      case 'rejected': return <Error />;
      default: return <Warning />;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!event) {
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="h6" color="text.secondary">
          Event not found
        </Typography>
      </Box>
    );
  }

  return (
    <Box maxWidth={1200} mx="auto" p={2}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/event-analytics')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" fontWeight={700} color="primary">
          Event Management
        </Typography>
        <Box ml="auto" display="flex" gap={1}>
          <Button
            variant={isEditing ? "outlined" : "contained"}
            startIcon={isEditing ? <Cancel /> : <Edit />}
            onClick={handleEditToggle}
            disabled={updating}
          >
            {isEditing ? 'Cancel' : 'Edit'}
          </Button>
          {isEditing && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<Save />}
              onClick={handleSave}
              disabled={updating}
            >
              Save
            </Button>
          )}
          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={handleDelete}
            disabled={updating}
          >
            Delete
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 3 }}>
        {/* Main Content */}
        <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 8' } }}>
          {/* Event Card */}
          <Card sx={{ mb: 3 }}>
            <Box sx={{ position: 'relative' }}>
              {event.images?.banner || event.banner ? (
                <CardMedia
                  component="img"
                  height="200"
                  image={event.images?.banner || event.banner}
                  alt={event.title}
                />
              ) : (
                <Box
                  sx={{
                    height: 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, #5b7fff 0%, #6ec6ff 100%)',
                  }}
                >
                  <Typography variant="h5" color="white" fontWeight={700}>
                    {event.title}
                  </Typography>
                </Box>
              )}
              
              <Box sx={{ position: 'absolute', top: 16, right: 16, display: 'flex', gap: 1 }}>
                {event.featured && (
                  <Chip
                    icon={<Star />}
                    label="Featured"
                    color="warning"
                    size="small"
                  />
                )}
                <Chip
                  icon={getStatusIcon(event.status)}
                  label={event.status}
                  color={getStatusColor(event.status) as any}
                  size="small"
                />
              </Box>
            </Box>

            <CardContent>
              <Typography variant="h5" fontWeight={600} mb={2}>
                {event.title}
              </Typography>
              
              {/* Admin Settings Section */}
              {isEditing ? (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight={600} mb={2}>
                    Admin Settings
                  </Typography>
                  
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 2 }}>
                    <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                      <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={editData.status}
                          onChange={(e) => setEditData(prev => ({ ...prev, status: e.target.value }))}
                          label="Status"
                        >
                          {statusOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    
                    <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={editData.featured}
                            onChange={(e) => setEditData(prev => ({ ...prev, featured: e.target.checked }))}
                          />
                        }
                        label="Featured"
                      />
                    </Box>
                    
                    <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={editData.takeRegistrations}
                            onChange={(e) => setEditData(prev => ({ ...prev, takeRegistrations: e.target.checked }))}
                          />
                        }
                        label="Take Registrations"
                      />
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight={600} mb={2}>
                    Admin Settings
                  </Typography>
                  
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 2 }}>
                    <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
                      <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                      <Chip
                        icon={getStatusIcon(event.status)}
                        label={event.status}
                        color={getStatusColor(event.status) as any}
                        size="small"
                      />
                    </Box>
                    
                    <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
                      <Typography variant="subtitle2" color="text.secondary">Featured</Typography>
                      <Chip
                        icon={event.featured ? <Star /> : undefined}
                        label={event.featured ? 'Yes' : 'No'}
                        color={event.featured ? 'warning' : 'default'}
                        size="small"
                      />
                    </Box>
                    
                    <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
                      <Typography variant="subtitle2" color="text.secondary">Take Registrations</Typography>
                      <Chip
                        icon={event.takeRegistrations !== false ? <CheckCircle /> : <Error />}
                        label={event.takeRegistrations !== false ? 'Yes' : 'No'}
                        color={event.takeRegistrations !== false ? 'success' : 'error'}
                        size="small"
                      />
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Description */}
              <Typography variant="h6" fontWeight={600} mb={2}>
                Description
              </Typography>
              <Box 
                sx={{ mb: 3 }}
                dangerouslySetInnerHTML={{ __html: event.description }}
              />

              {/* Event Details */}
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 2 }}>
                <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <CalendarToday sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="subtitle2" color="text.secondary">Start Date</Typography>
                  </Box>
                  <Typography variant="body2">
                    {new Date(event.dates?.startDate).toLocaleDateString()}
                  </Typography>
                </Box>
                
                <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <CalendarToday sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="subtitle2" color="text.secondary">End Date</Typography>
                  </Box>
                  <Typography variant="body2">
                    {new Date(event.dates?.endDate).toLocaleDateString()}
                  </Typography>
                </Box>
                
                <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <LocationOn sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="subtitle2" color="text.secondary">Location</Typography>
                  </Box>
                  <Typography variant="body2">
                    {event.location ? 
                      `${event.location.type}${event.location.address ? ` - ${event.location.address}` : ''}` 
                      : 'Online'
                    }
                  </Typography>
                </Box>
                
                <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <AttachMoney sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="subtitle2" color="text.secondary">Registration Fee</Typography>
                  </Box>
                  <Typography variant="body2">
                    {event.pricing?.type === 'free' ? 'Free' : `$${event.pricing?.amount || 0}`}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Admin Sponsors Section */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" fontWeight={600}>
                  Admin Sponsors ({sponsors.length})
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => handleOpenSponsorDialog()}
                  disabled={sponsorsLoading}
                >
                  Add Sponsor
                </Button>
              </Box>

              {sponsorsLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : sponsors.length === 0 ? (
                <Box textAlign="center" py={6}>
                  <Business sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" mb={1}>
                    No sponsors added yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Add your first sponsor to showcase event partnerships
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)'
                  },
                  gap: 3
                }}>
                  {sponsors.map((sponsor) => (
                    <Card 
                      key={sponsor._id}
                      variant="outlined" 
                      sx={{ 
                        height: '100%',
                        position: 'relative',
                        '&:hover': { boxShadow: 2 }
                      }}
                    >
                      {sponsor.imageUrl && (
                        <CardMedia
                          component="img"
                          height="120"
                          image={sponsor.imageUrl}
                          alt={sponsor.name}
                          sx={{ objectFit: 'contain', p: 1 }}
                        />
                      )}
                      
                      <CardContent sx={{ pb: '56px !important' }}>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                          {sponsor.name}
                        </Typography>
                        
                        {sponsor.website && (
                          <Box 
                            component="a"
                            href={sponsor.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              color: 'primary.main',
                              textDecoration: 'none',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                          >
                            <Language sx={{ mr: 1, fontSize: 16 }} />
                            <Typography variant="body2" noWrap>
                              Visit Website
                            </Typography>
                            <OpenInNew sx={{ ml: 1, fontSize: 16 }} />
                          </Box>
                        )}
                      </CardContent>

                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 8,
                          right: 8,
                          display: 'flex',
                          gap: 0.5
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleOpenSponsorDialog(sponsor)}
                          disabled={sponsorsLoading}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteSponsor(sponsor._id)}
                          disabled={sponsorsLoading}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </Card>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Sidebar */}
        <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 4' } }}>
          {/* Analytics Card */}
          {analytics && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} mb={2}>
                  Analytics
                </Typography>
                
                <Box display="flex" alignItems="center" mb={2}>
                  <People sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="body2">
                    {analytics.totalParticipants} participants
                  </Typography>
                </Box>
                
                <Box display="flex" alignItems="center" mb={2}>
                  <TrendingUp sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="body2">
                    {analytics.daysUntilStart > 0 ? `${analytics.daysUntilStart} days until start` : 'Started'}
                  </Typography>
                </Box>
                
                <Box display="flex" alignItems="center" mb={2}>
                  <CalendarToday sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="body2">
                    {analytics.daysUntilEnd > 0 ? `${analytics.daysUntilEnd} days until end` : 'Ended'}
                  </Typography>
                </Box>
                
                <Chip
                  label={
                    analytics.isActive ? 'Active' :
                    analytics.isUpcoming ? 'Upcoming' :
                    analytics.isCompleted ? 'Completed' :
                    analytics.isExpired ? 'Expired' : 'Unknown'
                  }
                  color={
                    analytics.isActive ? 'success' :
                    analytics.isUpcoming ? 'info' :
                    analytics.isCompleted ? 'default' :
                    analytics.isExpired ? 'error' : 'default'
                  }
                  size="small"
                />
              </CardContent>
            </Card>
          )}

          {/* Participants Card */}
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>
                Participants ({event.participants?.length || 0})
              </Typography>
              
              {event.participants && event.participants.length > 0 ? (
                <List>
                  {event.participants.slice(0, 5).map((participant: any) => (
                    <ListItem key={participant._id || participant.user?._id} dense>
                      <ListItemAvatar>
                        <Avatar src={participant.user?.profilePicture}>
                          {participant.user?.firstName?.[0] || '?'}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={`${participant.user?.firstName || 'Unknown'} ${participant.user?.lastName || ''}`}
                        secondary={`@${participant.user?.username || 'unknown'}`}
                      />
                    </ListItem>
                  ))}
                  {event.participants.length > 5 && (
                    <ListItem>
                      <ListItemText
                        primary={`+${event.participants.length - 5} more participants`}
                        primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                      />
                    </ListItem>
                  )}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No participants yet
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Add/Edit Sponsor Dialog */}
      <Dialog 
        open={sponsorDialogOpen} 
        onClose={handleCloseSponsorDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingSponsorId ? 'Edit Sponsor' : 'Add New Sponsor'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Sponsor Name *"
              value={sponsorFormData.name}
              onChange={(e) => handleSponsorInputChange('name', e.target.value)}
              error={!!sponsorErrors.name}
              helperText={sponsorErrors.name}
              margin="normal"
              InputProps={{
                startAdornment: <Language sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />

            <TextField
              fullWidth
              label="Website URL"
              value={sponsorFormData.website}
              onChange={(e) => handleSponsorInputChange('website', e.target.value)}
              error={!!sponsorErrors.website}
              helperText={sponsorErrors.website || 'Optional: Sponsor website URL'}
              margin="normal"
              InputProps={{
                startAdornment: <Language sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />

            <TextField
              fullWidth
              label="Logo/Image URL"
              value={sponsorFormData.imageUrl}
              onChange={(e) => handleSponsorInputChange('imageUrl', e.target.value)}
              error={!!sponsorErrors.imageUrl}
              helperText={sponsorErrors.imageUrl || 'Direct link to sponsor logo image'}
              margin="normal"
              InputProps={{
                startAdornment: <Image sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />

            {/* Image Preview */}
            {sponsorFormData.imageUrl && isValidUrl(sponsorFormData.imageUrl) && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  Logo Preview:
                </Typography>
                <img
                  src={sponsorFormData.imageUrl}
                  alt="Preview"
                  style={{
                    maxHeight: 100,
                    maxWidth: '100%',
                    objectFit: 'contain',
                    border: '1px solid #e0e0e0',
                    borderRadius: 4,
                    padding: 8
                  }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseSponsorDialog}
            disabled={sponsorSubmitting}
            startIcon={<Cancel />}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSponsorSubmit}
            variant="contained"
            disabled={sponsorSubmitting}
            startIcon={sponsorSubmitting ? <CircularProgress size={16} /> : <Save />}
          >
            {sponsorSubmitting ? 'Saving...' : editingSponsorId ? 'Update' : 'Add'} Sponsor
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EventDetailPage;